import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import {
    ATTACHMENT_KIND_BY_EXTENSION,
    ATTACHMENT_KINDS,
    INTERACTIVE_SERVICE_BASE_URL,
    MAX_ATTACHMENTS_PER_MESSAGE,
    MAX_ATTACHMENT_SIZE_BYTES,
} from '../constants';

export const ATTACHMENT_STATUS = {
    UPLOADING: 'uploading',
    READY: 'ready',
    ERROR: 'error',
};

export const ATTACHMENT_ERRORS = {
    TOO_LARGE: 'too_large',
    UNSUPPORTED_TYPE: 'unsupported_type',
    TOO_MANY: 'too_many',
    UPLOAD_FAILED: 'upload_failed',
    RATE_LIMITED: 'rate_limited',
    REJECTED: 'rejected',
};

// Only the failures a second attempt could survive. A file the server or the
// client judged on its own merits is judged the same way next time, so offering
// a retry there is a lie; "too many" depends on the queue, which the user can
// change, so that chip keeps its retry for once a slot frees.
const RETRYABLE_ERRORS = [ATTACHMENT_ERRORS.UPLOAD_FAILED, ATTACHMENT_ERRORS.RATE_LIMITED, ATTACHMENT_ERRORS.TOO_MANY];

// The server's deterministic rejections, mapped to the chip's own wording. 429
// is the one 4xx worth retrying: the window is what changed, not the file.
const ERROR_BY_STATUS = {
    413: ATTACHMENT_ERRORS.TOO_LARGE,
    415: ATTACHMENT_ERRORS.UNSUPPORTED_TYPE,
    429: ATTACHMENT_ERRORS.RATE_LIMITED,
};

const errorForRejection = status => {
    if (ERROR_BY_STATUS[status]) return ERROR_BY_STATUS[status];
    // Any other 4xx is the server's verdict on this file: retrying repeats it.
    return status >= 400 && status < 500 ? ATTACHMENT_ERRORS.REJECTED : ATTACHMENT_ERRORS.UPLOAD_FAILED;
};

export const isRetryableAttachment = attachment =>
    attachment?.status === ATTACHMENT_STATUS.ERROR &&
    RETRYABLE_ERRORS.includes(attachment.error) &&
    Boolean(attachment.file);

// The chip shows the server's reason when there is one: "Zip archives are not
// supported" beats a bare "Upload failed".
const uploadRejection = async response => {
    const error = new Error(`Upload failed: ${response.status}`);
    error.status = response.status;
    try {
        error.detail = (await response.json())?.detail || null;
    } catch {
        error.detail = null;
    }
    return error;
};

const kindForFile = file => {
    const extension = file.name?.includes('.')
        ? file.name
              .split('.')
              .pop()
              .toLowerCase()
        : '';
    return ATTACHMENT_KIND_BY_EXTENSION[extension] || null;
};

let attachmentLocalId = 0;

// The composer's pending attachments. Files upload as soon as they are picked;
// send is expected to wait for `isUploading`.
const useAttachments = () => {
    const [attachments, setAttachments] = useState([]);

    // The committed list, so async callbacks (upload completion, unmount) see the
    // current attachments rather than a stale render closure.
    const attachmentsRef = useRef([]);
    const commit = useCallback(next => {
        attachmentsRef.current = next;
        setAttachments(next);
    }, []);

    // Revoked on unmount rather than on send, so the optimistic user message
    // keeps its thumbnail.
    const previewUrlsRef = useRef(new Set());
    const revokePreviews = useCallback(() => {
        previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
        previewUrlsRef.current.clear();
    }, []);

    // Deletes a staged upload that nothing references anymore: without this the
    // S3 object outlives its one-hour registry record. Best effort - no run
    // depends on it, and it is a no-op once a run has consumed the id.
    const releaseUpload = useCallback(uploadId => {
        if (!uploadId) return;
        interactiveServiceFetch(`${INTERACTIVE_SERVICE_BASE_URL}/uploads/${uploadId}`, {
            method: 'DELETE',
        }).catch(() => {});
    }, []);

    // Everything still staged dies with the composer. Emptying the ref also
    // routes uploads that complete after unmount into uploadFile's
    // chip-removed release.
    useEffect(
        () => () => {
            attachmentsRef.current.forEach(att => releaseUpload(att.uploadId));
            attachmentsRef.current = [];
            revokePreviews();
        },
        [releaseUpload, revokePreviews]
    );

    const updateAttachment = useCallback(
        (localId, patch) => {
            commit(attachmentsRef.current.map(att => (att.localId === localId ? { ...att, ...patch } : att)));
        },
        [commit]
    );

    const uploadFile = useCallback(
        async (localId, file, kind) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('kind', kind);
            try {
                // No explicit Content-Type: the browser must set the multipart boundary.
                const response = await interactiveServiceFetch(`${INTERACTIVE_SERVICE_BASE_URL}/uploads`, {
                    method: 'POST',
                    body: formData,
                });
                if (!response.ok) throw await uploadRejection(response);
                const result = await response.json();
                // Chip removed while this was in flight: the id lands nowhere, so
                // release it here or nothing ever will.
                if (!attachmentsRef.current.some(att => att.localId === localId)) {
                    releaseUpload(result.upload_id);
                    return;
                }
                updateAttachment(localId, {
                    status: ATTACHMENT_STATUS.READY,
                    uploadId: result.upload_id,
                    name: result.name,
                    kind: result.kind,
                    sizeBytes: result.size_bytes,
                });
            } catch (err) {
                // 429 keeps its own translated message: the server's "try again later"
                // never says the window is a minute, which is the one thing that helps.
                const error = errorForRejection(err?.status);
                updateAttachment(localId, {
                    status: ATTACHMENT_STATUS.ERROR,
                    error,
                    errorMessage: error === ATTACHMENT_ERRORS.RATE_LIMITED ? null : err?.detail || null,
                });
            }
        },
        [releaseUpload, updateAttachment]
    );

    const addFiles = useCallback(
        files => {
            const incoming = Array.from(files || []);
            if (!incoming.length) return;

            const { current } = attachmentsRef;
            let nonErrorCount = current.filter(att => att.status !== ATTACHMENT_STATUS.ERROR).length;
            const added = [];
            const uploads = [];
            incoming.forEach(file => {
                attachmentLocalId += 1;
                const localId = `att-${attachmentLocalId}`;
                const kind = kindForFile(file);
                const base = {
                    localId,
                    file,
                    name: file.name,
                    kind,
                    sizeBytes: file.size,
                    uploadId: null,
                    previewUrl: null,
                };
                if (!kind) {
                    added.push({
                        ...base,
                        status: ATTACHMENT_STATUS.ERROR,
                        error: ATTACHMENT_ERRORS.UNSUPPORTED_TYPE,
                    });
                    return;
                }
                if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
                    added.push({ ...base, status: ATTACHMENT_STATUS.ERROR, error: ATTACHMENT_ERRORS.TOO_LARGE });
                    return;
                }
                if (nonErrorCount >= MAX_ATTACHMENTS_PER_MESSAGE) {
                    added.push({ ...base, status: ATTACHMENT_STATUS.ERROR, error: ATTACHMENT_ERRORS.TOO_MANY });
                    return;
                }
                nonErrorCount += 1;
                let previewUrl = null;
                if (kind === ATTACHMENT_KINDS.IMAGE) {
                    previewUrl = URL.createObjectURL(file);
                    previewUrlsRef.current.add(previewUrl);
                }
                added.push({ ...base, previewUrl, status: ATTACHMENT_STATUS.UPLOADING, error: null });
                uploads.push({ localId, file, kind });
            });

            commit([...current, ...added]);
            uploads.forEach(({ localId, file, kind }) => uploadFile(localId, file, kind));
        },
        [commit, uploadFile]
    );

    const retryAttachment = useCallback(
        localId => {
            const attachment = attachmentsRef.current.find(att => att.localId === localId);
            if (!isRetryableAttachment(attachment)) return;
            // An error chip does not count against the cap, so retrying one that
            // sat there while the user filled the queue has to be caught here.
            // It stays retryable, so freeing a slot makes the same file usable.
            const nonErrorCount = attachmentsRef.current.filter(att => att.status !== ATTACHMENT_STATUS.ERROR).length;
            if (nonErrorCount >= MAX_ATTACHMENTS_PER_MESSAGE) {
                updateAttachment(localId, { error: ATTACHMENT_ERRORS.TOO_MANY, errorMessage: null });
                return;
            }
            updateAttachment(localId, { status: ATTACHMENT_STATUS.UPLOADING, error: null, errorMessage: null });
            uploadFile(localId, attachment.file, attachment.kind);
        },
        [updateAttachment, uploadFile]
    );

    const removeAttachment = useCallback(
        localId => {
            const removed = attachmentsRef.current.find(att => att.localId === localId);
            if (!removed) return;
            releaseUpload(removed.uploadId);
            commit(attachmentsRef.current.filter(att => att.localId !== localId));
        },
        [commit, releaseUpload]
    );

    // Called once a run was created for `sentLocalIds`, so those ids belong to it
    // now and must not be released. Preview URLs stay alive - the optimistic user
    // message's chips reference them.
    const clearAttachments = useCallback(
        sentLocalIds => {
            commit(
                attachmentsRef.current.filter(
                    att => att.status !== ATTACHMENT_STATUS.ERROR && !sentLocalIds.includes(att.localId)
                )
            );
        },
        [commit]
    );

    // Session switch: the messages holding the sent chips leave with it, so even
    // the URLs clearAttachments kept for them are now unreferenced.
    const discardAttachments = useCallback(() => {
        attachmentsRef.current.forEach(att => releaseUpload(att.uploadId));
        revokePreviews();
        if (attachmentsRef.current.length) commit([]);
    }, [commit, releaseUpload, revokePreviews]);

    const readyAttachments = useMemo(() => attachments.filter(att => att.status === ATTACHMENT_STATUS.READY), [
        attachments,
    ]);
    const readyUploadIds = useMemo(() => readyAttachments.map(att => att.uploadId), [readyAttachments]);
    const isUploading = attachments.some(att => att.status === ATTACHMENT_STATUS.UPLOADING);

    return useMemo(
        () => ({
            attachments,
            addFiles,
            removeAttachment,
            retryAttachment,
            clearAttachments,
            discardAttachments,
            readyAttachments,
            readyUploadIds,
            isUploading,
        }),
        [
            attachments,
            addFiles,
            removeAttachment,
            retryAttachment,
            clearAttachments,
            discardAttachments,
            readyAttachments,
            readyUploadIds,
            isUploading,
        ]
    );
};

export const attachmentsApiPropType = PropTypes.shape({
    attachments: PropTypes.arrayOf(
        PropTypes.shape({
            localId: PropTypes.string.isRequired,
            name: PropTypes.string,
            kind: PropTypes.string,
            sizeBytes: PropTypes.number,
            status: PropTypes.oneOf(Object.values(ATTACHMENT_STATUS)),
            error: PropTypes.string,
            errorMessage: PropTypes.string,
            previewUrl: PropTypes.string,
        })
    ).isRequired,
    addFiles: PropTypes.func.isRequired,
    removeAttachment: PropTypes.func.isRequired,
    retryAttachment: PropTypes.func.isRequired,
    readyUploadIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    isUploading: PropTypes.bool.isRequired,
});

export default useAttachments;
